import type { FieldMissionItem } from '@groupe-reca/contracts'
import { metersBetween, ringAt, type LatLng, type Ring } from '@/features/detection/domain/geometry'
import type { Radii } from '@/features/detection/domain/geometry'
import type { PublicStatus } from '@/features/detection/domain/types'
import { hasCoordinates, itemAddress } from '../domain/fieldText'

// Diagnostic de calibration (TEMPORAIRE, à retirer après l'observation terrain du lot O7) : distances de la tablette
// au drapeau de chaque résidence (le seul point utilisé par le moteur) et à sa pastille d'adresse.

export interface DebugRow {
  itemId: string
  order: number
  address: string
  flag: LatLng | null
  /** Distance tablette → drapeau (m); `null` sans drapeau ou sans position. */
  flagDistanceM: number | null
  /** Distance tablette → pastille d'adresse (m). */
  markerDistanceM: number | null
  /** Écart pastille → drapeau (m) : la pastille n'est jamais le point de détection. */
  flagOffsetM: number | null
  ring: Ring | null
  status: PublicStatus | null
  hits: number
  disarmed: boolean
}

export interface DebugEngine {
  statuses: Record<string, PublicStatus>
  hits: Record<string, number>
  disarmed: readonly string[]
}

export function debugRows(
  items: readonly FieldMissionItem[],
  position: LatLng | null,
  radii: Radii,
  engine: DebugEngine | null,
): DebugRow[] {
  const rows = items.map((item): DebugRow => {
    const flag = item.snapshot.flag ? { lat: item.snapshot.flag.latitude, lng: item.snapshot.flag.longitude } : null
    const marker = hasCoordinates(item.snapshot)
      ? { lat: item.snapshot.latitude ?? 0, lng: item.snapshot.longitude ?? 0 }
      : null
    const flagDistanceM = flag && position ? metersBetween(flag, position) : null
    return {
      itemId: item.id,
      order: item.order,
      address: itemAddress(item.snapshot),
      flag,
      flagDistanceM,
      markerDistanceM: marker && position ? metersBetween(marker, position) : null,
      flagOffsetM: flag && marker ? metersBetween(flag, marker) : null,
      ring: flagDistanceM === null ? null : ringAt(flagDistanceM, radii),
      status: engine?.statuses[item.id] ?? null,
      hits: engine?.hits[item.id] ?? 0,
      disarmed: engine?.disarmed.includes(item.id) ?? false,
    }
  })
  // Les plus proches du drapeau d'abord, puis les résidences sans drapeau ou sans position, dans l'ordre de la route.
  return rows.sort((a, b) => {
    if (a.flagDistanceM !== null && b.flagDistanceM !== null) return a.flagDistanceM - b.flagDistanceM
    if (a.flagDistanceM !== null) return -1
    if (b.flagDistanceM !== null) return 1
    return a.order - b.order
  })
}
